import { boolToRadio, radioToNullableBool } from "@/lib/helpers/radio-adapters";
import dayjs from "@/lib/helpers/dayjs";

import { ACP_BOOLEAN_RADIO_FIELDS } from "../constants";
import type { NoteContent, NoteWithContent } from "../types";

export const adaptAcaaData = (note: Nullable<NoteWithContent>): Nullable<NoteWithContent> => {
  if (!note) return note; // defensive: note may be null upstream

  // shallow clone; content[item] reassigned below only
  const copyOfNote: NoteWithContent & { content: NoteContent } = {
    ...note,
    content: { ...note.content },
  };

  // Backend stores ACAA yes/no fields as booleans; form radios need
  // "Yes"/"No" strings. Maps boolean -> string for read-side use
  ACP_BOOLEAN_RADIO_FIELDS.forEach((item: string) => {
    copyOfNote.content[item] = boolToRadio(copyOfNote.content[item]);
  });

  // session_date: backend uses "MM/DD/YYYY", form field expects
  // "MMDDYYYY" digits-only. Read-side half of that round-trip.
  const sessionDate = copyOfNote.session_date;

  if (typeof sessionDate === "string" && sessionDate.includes("/")) {
    // strict mode rejects invalid calendar dates instead of rolling them over
    const parsed = dayjs(sessionDate, "MM/DD/YYYY", true);

    if (parsed.isValid()) {
      copyOfNote.session_date = parsed.format("MMDDYYYY");
    }
  }

  return copyOfNote;
};

// casts "Yes"/"No" radio strings back to booleans before submission.
// A cleared/unanswered radio ("" sentinel) drops the key entirely rather
// than sending null — backend's finalize schema requires these fields be
// real booleans when present (regardless of attendance), but tolerates
// them being omitted outright when unanswered (ASD-1103 no-show path).
export const adaptRadioFields = (content: NoteContent): NoteContent => {
  const copyOfContent = { ...content };

  ACP_BOOLEAN_RADIO_FIELDS.forEach((field: string) => {
    const value = radioToNullableBool(copyOfContent[field]);

    if (value === null) {
      delete copyOfContent[field];
    } else {
      copyOfContent[field] = value;
    }
  });

  return copyOfContent;
};
